import { TAG_WIDGET_IDS, WidgetType } from 'constants/loanApplication';

import { flattenHeaderColumns } from '../../HeaderGrid/HeaderGrid.utils';
import { STATUS_FIELD_NAME } from '../../HeaderItemValue/HeaderItemValue.constants';
import { checkFieldVisibility } from '../../LoanHeaders.utils';

import { IDENTITY_FIELD_NAMES } from './HeaderViewList.constants';

const getColumnItems = column => column.cells.flatMap(cell => cell.items);

export const getHeaderItemKey = item => item.id;

const IDENTITY_WIDGET_IDS = [
  WidgetType.LOAN_NUMBER.id,
  WidgetType.MILESTONE_STATUS.id,
  WidgetType.LOAN_ROLES.id,
  ...TAG_WIDGET_IDS,
];

// A company may expose the loan number either as the LOAN_NUMBER widget or as a plain (often
// unlabeled) field. Match the plain field by value so it joins the identity line in both cases.
export const getIsLoanNumberField = (item, loanNumber) =>
  !!loanNumber && item.dynamicFieldId !== WidgetType.LOAN_NUMBER.id && item.value === loanNumber;

/**
 * The identity line carries the loan's "who / what / where in the flow" — the loan-number,
 * milestone-status and loan-roles widgets plus the borrower and the status field (promoted to the
 * status widget). Everything else is a metric and stays on the second line.
 */
export const getIsIdentityHeaderItem = (item, loanNumber) =>
  IDENTITY_WIDGET_IDS.includes(item.dynamicFieldId) ||
  IDENTITY_FIELD_NAMES.includes(item.normalizedName) ||
  item.normalizedName === STATUS_FIELD_NAME ||
  getIsLoanNumberField(item, loanNumber);

// Identity-line render order, top to bottom: loan number, borrower, milestone status (widget),
// archived / test-loan tags, then the assignee avatars. Unmatched items fall to the end preserving
// config order.
const IDENTITY_ORDER_MATCHERS = [
  (item, loanNumber) =>
    item.dynamicFieldId === WidgetType.LOAN_NUMBER.id || getIsLoanNumberField(item, loanNumber),
  item => IDENTITY_FIELD_NAMES.includes(item.normalizedName),
  item =>
    item.dynamicFieldId === WidgetType.MILESTONE_STATUS.id ||
    item.normalizedName === STATUS_FIELD_NAME,
  item => TAG_WIDGET_IDS.includes(item.dynamicFieldId),
  item => item.dynamicFieldId === WidgetType.LOAN_ROLES.id,
];

const getIdentityItemOrder = (item, loanNumber) => {
  const index = IDENTITY_ORDER_MATCHERS.findIndex(match => match(item, loanNumber));

  return index === -1 ? IDENTITY_ORDER_MATCHERS.length : index;
};

const removeIdentityItemsFromColumn = (column, loanNumber) => ({
  ...column,
  cells: column.cells
    .map(cell => ({
      ...cell,
      items: cell.items.filter(item => !getIsIdentityHeaderItem(item, loanNumber)),
    }))
    .filter(cell => cell.items.length > 0),
});

/**
 * Split the primary header view into the two rendered lines: a flat, priority-ordered list of
 * identity items (line 1) and the remaining columns (line 2), preserving the configured column /
 * divider structure for the metrics line so its overflow handling is unchanged.
 */
export const partitionPrimaryHeaderColumns = (columns, loanNumber = null) => {
  const identityItems = flattenHeaderColumns(columns)
    .filter(item => getIsIdentityHeaderItem(item, loanNumber))
    .sort((a, b) => getIdentityItemOrder(a, loanNumber) - getIdentityItemOrder(b, loanNumber));
  const metricColumns = (columns ?? [])
    .map(column => removeIdentityItemsFromColumn(column, loanNumber))
    .filter(column => column.cells.length > 0);

  return { identityItems, metricColumns };
};

/**
 * On mobile the primary header wraps one field per row, so the full set (often 12-15 fields) eats
 * 30-50% of the screen. Keep only the leading columns whose fields fit within `maxItems` and surface
 * the rest behind the "More" popover. Always keeps at least the first column.
 */
export const getMobileHeaderColumns = (columns, maxItems) => {
  const cumulativeItemCounts = columns.reduce((acc, column) => {
    const previousTotal = acc[acc.length - 1] ?? 0;

    return [...acc, previousTotal + getColumnItems(column).length];
  }, []);

  const displayedColumns = columns.filter((column, index) => {
    const itemsBeforeColumn = index === 0 ? 0 : cumulativeItemCounts[index - 1];

    return itemsBeforeColumn < maxItems;
  });

  return {
    displayedColumns,
    hasOverflow: displayedColumns.length < columns.length,
  };
};

const filterCellItems = ({ cell, isApplicationLoaded, isReportsAllowed, hasAssignedUsers }) =>
  cell.items.filter(item => {
    const isFieldVisible = checkFieldVisibility({
      isApplicationLoaded,
      isReportsAllowed,
      field: item.normalizedName,
    });

    if (!isFieldVisible) {
      return false;
    }

    const isLoanRolesWidget = item.dynamicFieldId === WidgetType.LOAN_ROLES.id;

    if (isLoanRolesWidget && !hasAssignedUsers) {
      return false;
    }

    const isTagWidget = TAG_WIDGET_IDS.includes(item.dynamicFieldId);

    if (isTagWidget && !item.value) {
      return false;
    }

    return true;
  });

export const filterRenderableHeaderColumns = ({
  columns,
  isApplicationLoaded,
  isReportsAllowed,
  hasAssignedUsers,
}) => {
  if (!columns?.length) {
    return [];
  }

  return columns
    .map(column => ({
      ...column,
      cells: column.cells
        .map(cell => ({
          ...cell,
          items: filterCellItems({ cell, isApplicationLoaded, isReportsAllowed, hasAssignedUsers }),
        }))
        .filter(cell => cell.items.length > 0),
    }))
    .filter(column => column.cells.length > 0);
};
